import React, { useEffect, useRef, useState } from 'react';
import { GitBranch, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';
import { useTheme } from '../../../hooks/useTheme';
import {
  Dependency,
  DependencyType,

  DependencySeverity
} from '../../../types';

interface DependencyVisualizationProps {
  dependencies: Dependency[];
}

interface Node {
  id: string;
  name: string;
  type: string;
  x: number;
  y: number;
  dependencies: Dependency[];
}

interface Edge {
  source: string;
  target: string;
  dependency: Dependency;
}

// Theme-aware color getter for SVG elements
const getThemeColor = (varName: string): string => {
  if (typeof window !== 'undefined') {
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || varName;
  }
  return varName;
};

export const DependencyVisualization: React.FC<DependencyVisualizationProps> = ({
  dependencies,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const { theme } = useTheme();

  useEffect(() => {
    if (dependencies.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    // Extract unique entities from dependencies
    const entityMap = new Map<string, Node>();
    
    dependencies.forEach(dep => {
      // Add source entity
      const sourceKey = `${dep.sourceType}-${dep.sourceId}`;
      if (!entityMap.has(sourceKey)) {
        entityMap.set(sourceKey, {
          id: sourceKey,
          name: dep.sourceName || 'Unknown',
          type: dep.sourceType,
          x: 0,
          y: 0,
          dependencies: [],
        });
      }
      entityMap.get(sourceKey)!.dependencies.push(dep);
      
      // Add target entity
      const targetKey = `${dep.targetType}-${dep.targetId}`;
      if (!entityMap.has(targetKey)) {
        entityMap.set(targetKey, {
          id: targetKey,
          name: dep.targetName || 'Unknown',
          type: dep.targetType,
          x: 0,
          y: 0,
          dependencies: [],
        });
      }
      entityMap.get(targetKey)!.dependencies.push(dep);
    });

    const nodeArray = Array.from(entityMap.values());
    
    // Simple circular layout
    const centerX = 400;
    const centerY = 300;
    const radius = Math.min(200, Math.max(100, nodeArray.length * 20));
    
    nodeArray.forEach((node, index) => {
      const angle = (index / nodeArray.length) * 2 * Math.PI;
      node.x = centerX + radius * Math.cos(angle);
      node.y = centerY + radius * Math.sin(angle);
    });

    // Create edges
    const edgeArray: Edge[] = dependencies.map(dep => ({
      source: `${dep.sourceType}-${dep.sourceId}`,
      target: `${dep.targetType}-${dep.targetId}`,
      dependency: dep,
    }));

    setNodes(nodeArray);
    setEdges(edgeArray);
  }, [dependencies]);

  // Get theme-aware colors for SVG elements
  const colors = React.useMemo(() => ({
    red: getThemeColor('--chart-red'),
    orange: getThemeColor('--chart-orange'),
    blue: getThemeColor('--chart-blue'),
    green: getThemeColor('--chart-green'),
    yellow: getThemeColor('--chart-yellow'),
    slate: getThemeColor('--chart-slate'),
    text: getThemeColor('--chart-text'),
    nodeFill: getThemeColor('--chart-node-fill'),
    nodeStroke: getThemeColor('--chart-node-stroke'),
  }), [theme]);

  const getNodeColor = (node: Node) => {
    const criticalDeps = node.dependencies.filter(d => d.severity === DependencySeverity.CRITICAL);
    const highDeps = node.dependencies.filter(d => d.severity === DependencySeverity.HIGH);

    if (criticalDeps.length > 0) return colors.red;
    if (highDeps.length > 0) return colors.orange;
    return colors.blue;
  };

  const getEdgeColor = (dependency: Dependency) => {
    switch (dependency.severity) {
      case DependencySeverity.CRITICAL:
        return colors.red;
      case DependencySeverity.HIGH:
        return colors.orange;
      case DependencySeverity.MEDIUM:
        return colors.yellow;
      case DependencySeverity.LOW:
        return colors.green;
      default:
        return colors.slate;
    }
  };

  const getEdgeStyle = (dependency: Dependency) => {
    switch (dependency.type) {
      case DependencyType.BLOCKING:
        return 'solid';
      case DependencyType.ENABLING:
        return 'dashed';
      case DependencyType.INFORMATIONAL:
        return 'dotted';
      default:
        return 'solid';
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.3));
  };

  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setSelectedNode(null);
  };

  const formatEntityType = (entityType: string) => {
    return entityType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  if (dependencies.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-muted/30 rounded-lg border-2 border-dashed border-border">
        <div className="text-center">
          <GitBranch className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No dependencies to visualize</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create dependencies to see the relationship graph
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleZoomIn}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleZoomOut}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Zoom: {Math.round(zoom * 100)}%
          </span>
        </div>
        
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-chart-red rounded-full"></div>
            <span className="text-muted-foreground">Critical</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-chart-orange rounded-full"></div>
            <span className="text-muted-foreground">High</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-chart-blue rounded-full"></div>
            <span className="text-muted-foreground">Normal</span>
          </div>
        </div>
      </div>

      {/* Graph */}
      <div className="relative bg-bg-primary rounded-lg border border-border overflow-hidden">
        <svg
          ref={svgRef}
          width="800"
          height="600"
          className="cursor-move"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
            {/* Edges */}
            {edges.map((edge, index) => {
              const sourceNode = nodes.find(n => n.id === edge.source);
              const targetNode = nodes.find(n => n.id === edge.target);
              
              if (!sourceNode || !targetNode) return null;
              
              return (
                <g key={index}>
                  <line
                    x1={sourceNode.x}
                    y1={sourceNode.y}
                    x2={targetNode.x}
                    y2={targetNode.y}
                    stroke={getEdgeColor(edge.dependency)}
                    strokeWidth="2"
                    strokeDasharray={getEdgeStyle(edge.dependency) === 'dashed' ? '5,5' : 
                                   getEdgeStyle(edge.dependency) === 'dotted' ? '2,2' : 'none'}
                    markerEnd="url(#arrowhead)"
                  />
                  
                  {/* Edge label */}
                  <text
                    x={(sourceNode.x + targetNode.x) / 2}
                    y={(sourceNode.y + targetNode.y) / 2 - 5}
                    fill={colors.slate}
                    fontSize="10"
                    textAnchor="middle"
                    className="pointer-events-none"
                  >
                    {edge.dependency.type}
                  </text>
                </g>
              );
            })}
            
            {/* Nodes */}
            {nodes.map((node) => (
              <g key={node.id}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="20"
                  fill={getNodeColor(node)}
                  stroke={selectedNode?.id === node.id ? colors.text : 'transparent'}
                  strokeWidth="2"
                  className="cursor-pointer hover:opacity-80"
                  onClick={() => setSelectedNode(selectedNode?.id === node.id ? null : node)}
                />

                <text
                  x={node.x}
                  y={node.y + 35}
                  fill={colors.text}
                  fontSize="12"
                  textAnchor="middle"
                  className="pointer-events-none font-medium"
                >
                  {node.name.length > 15 ? `${node.name.substring(0, 15)}...` : node.name}
                </text>

                <text
                  x={node.x}
                  y={node.y + 50}
                  fill={colors.slate}
                  fontSize="10"
                  textAnchor="middle"
                  className="pointer-events-none"
                >
                  {formatEntityType(node.type)}
                </text>
              </g>
            ))}
          </g>
          
          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill={colors.slate}
              />
            </marker>
          </defs>
        </svg>
        
        {/* Node Details Panel */}
        {selectedNode && (
          <div className="absolute top-4 right-4 bg-muted border border-border rounded-lg p-4 max-w-xs">
            <h4 className="font-medium text-foreground mb-2">{selectedNode.name}</h4>
            <p className="text-sm text-muted-foreground mb-3">
              {formatEntityType(selectedNode.type)}
            </p>
            
            <div className="space-y-2">
              <div className="text-sm">
                <span className="text-muted-foreground">Dependencies: </span>
                <span className="text-foreground">{selectedNode.dependencies.length}</span>
              </div>
              
              {selectedNode.dependencies.length > 0 && (
                <div className="space-y-1">
                  {selectedNode.dependencies.slice(0, 3).map((dep, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Badge 
                        variant={dep.severity === DependencySeverity.CRITICAL ? 'danger' : 
                                dep.severity === DependencySeverity.HIGH ? 'warning' : 'secondary'}
                        className="text-xs"
                      >
                        {dep.severity}
                      </Badge>
                      <span className="text-xs text-foreground truncate">
                        {dep.name}
                      </span>
                    </div>
                  ))}
                  {selectedNode.dependencies.length > 3 && (
                    <div className="text-xs text-muted-foreground">
                      +{selectedNode.dependencies.length - 3} more
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="font-medium text-foreground mb-3">Legend</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h5 className="text-foreground font-medium mb-2">Dependency Types</h5>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-0.5 bg-chart-slate"></div>
                <span className="text-muted-foreground">Blocking</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-0.5 bg-chart-slate border-dashed border-t border-chart-slate"></div>
                <span className="text-muted-foreground">Enabling</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-0.5 bg-chart-slate opacity-75"></div>
                <span className="text-muted-foreground">Informational</span>
              </div>
            </div>
          </div>

          <div>
            <h5 className="text-foreground font-medium mb-2">Severity Levels</h5>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-chart-red rounded-full"></div>
                <span className="text-muted-foreground">Critical</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-chart-orange rounded-full"></div>
                <span className="text-muted-foreground">High</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-chart-yellow rounded-full"></div>
                <span className="text-muted-foreground">Medium</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-chart-green rounded-full"></div>
                <span className="text-muted-foreground">Low</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};